import React from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Box, MenuItem, Stack } from "@mui/material";
import Textarea from "@mui/joy/Textarea";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import Field from "../../../../components/field/index";
import { useResponsive } from "../../../../hooks/useResponsive";
import { useCustomSearchParams } from "../../../../hooks/useCustomSearchParams";
import { createCard } from "../../../../sevices/apiCards";
import useToastPromise from "../../../../hooks/useToast";
import CustomMenuItem from "../../../../components/Menu/CustomMenuItem";
import { CustomButton } from "../../../../components/Button/Button";
import useBoardIdByKey from "../../../../hooks/useBoardIdByKey";

function Form({ onClose, workflowSteps }) {
  const queryClient = useQueryClient();
  const [milestone, setMilestone] = useCustomSearchParams("milestone");
  const { id } = useBoardIdByKey();
  const isMobile = useResponsive("down", "md");
  const { showToastPromise } = useToastPromise();

  const schema = yup.object().shape({
    title: yup.string().required("Title is required"),
    workflow_step: yup.string().required("workflow step is required"),
  });

  const { register, handleSubmit, reset, getValues, formState } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      workflow_step: workflowSteps?.sort(
        (a, b) => a.step_sequence - b.step_sequence
      )[0].workflow_step_id,
    },
  });

  const { errors } = formState;

  const { mutate: createMutate, isPending } = useMutation({
    mutationFn: async (data) => {
      return await showToastPromise(createCard, data);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: [`cards`, id],
      });
      onClose();
    },
    onError: (error) => {
      console.error("An error occurred while creating task", error);
    },
  });

  const submit = (data) => {
    let newdata = {};
    if (milestone) {
      newdata = {
        title: data.title,
        workflow_step_id: data.workflow_step,
        board_id: id,
        epic_id: milestone ? milestone : "",
      };
    } else {
      newdata = {
        title: data.title,
        workflow_step_id: data.workflow_step,
        board_id: id,
      };
    }

    if (data.title) {
      createMutate(newdata);
      queryClient.invalidateQueries({
        queryKey: ["epic_id", milestone],
      });
      queryClient.invalidateQueries({
        queryKey: ["milestone_history", milestone],
      });
    }
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(submit)}
        noValidate
        className={`listview-form ${
          isMobile ? "listview-mobile-padding" : "listview-desktop-padding"
        }`}>
        <Stack spacing={3}>
          <Textarea
            placeholder="What needs to be done?"
            size="lg"
            required
            id="title"
            name="title"
            minRows={4}
            error={!!errors?.title}
            {...register("title")}
          />
          {errors?.title && (
            <Box className="listview-error-message">{errors.title.message}</Box>
          )}
          <Field
            label={"Workflow_step"}
            required
            id="workflow_step"
            name="workflow_step"
            error={errors?.workflow_step ? true : false}
            helperText={errors?.workflow_step && errors.workflow_step.message}
            select
            defaultValue={
              workflowSteps?.sort(
                (a, b) => a.step_sequence - b.step_sequence
              )[0].workflow_step_id
            }
            {...register("workflow_step")}>
            {workflowSteps?.map((option) => (
              <CustomMenuItem
                key={`${option.workflow_step_id}`}
                id={`${option.workflow_step_id}`}
                value={option.workflow_step_id}>
                {option.status}
                </CustomMenuItem>
            ))}
          </Field>

          <Box className="listview-button-container d-flex">
            <CustomButton
              type="submit"
              variant="contained"
              className="listview-create-button"
              disabled={isPending}>
              Create
            </CustomButton>
            <CustomButton
              variant="outlined"
              className="listview-cancel-button"
              onClick={onClose}
              disabled={isPending}>
              Cancel
            </CustomButton>
          </Box>
        </Stack>
      </Box>
    </>
  );
}

export default Form;
